import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useStorePos } from "./useStorePos";
import {
  buscarMercaderiasPorTexto,
  crearControladorBusquedaMercaderias,
  crearFiltrosMercaderiaDesdeTexto,
  evaluarFiltrosMercaderia,
} from "../../servicios/mercaderiasPiSeun";

const obtenerStock = (producto) => {
  const raw = producto?.sExtra ?? producto?.Stock ?? producto?.stock ?? 0;
  const parsed = Number.parseFloat(String(raw).replace(",", "."));
  return Number.isFinite(parsed) ? parsed : 0;
};

const formatStock = (value, locale) =>
  new Intl.NumberFormat(locale, {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }).format(Number(value) || 0);

const obtenerEstadoStock = (stock, t) => {
  if (stock <= 0) return t("pos.outOfStock");
  if (stock <= 5) return t("products.lowStock");
  return t("products.available");
};

const obtenerClaseEstado = (stock) => {
  if (stock <= 0) return "sin-stock";
  if (stock <= 5) return "bajo-stock";
  return "disponible";
};

const PosPopupStock = () => {
  const { t, i18n } = useTranslation();
  const locale = i18n.resolvedLanguage === "en" ? "en-US" : "es-AR";
  const ListaMercaderias = useStorePos((s) => s.ListaMercaderias);
  const BuscandoMercaderia = useStorePos((s) => s.BuscandoMercaderia);
  const getMercaderia = useStorePos((s) => s.getMercaderia);
  const cargarCatalogoMercaderias = useStorePos((s) => s.cargarCatalogoMercaderias);
  const [busqueda, setBusqueda] = useState("");
  const [busquedaRealizada, setBusquedaRealizada] = useState(false);
  const [errorBusqueda, setErrorBusqueda] = useState("");
  const [filtro, setFiltro] = useState("todos");

  const controladorBusqueda = useMemo(() => crearControladorBusquedaMercaderias({
    consultar: async (filtros, signal) => {
      return buscarMercaderiasPorTexto(getMercaderia, filtros.codigo, signal);
    },
    alLimpiar: () => {
      useStorePos.getState().limpiarResultadosMercaderias();
      setBusquedaRealizada(false);
      setErrorBusqueda("");
    },
    alBuscar: () => setErrorBusqueda(""),
    alResolver: () => setBusquedaRealizada(true),
    alError: (error) => {
      setBusquedaRealizada(true);
      setErrorBusqueda(error?.message || t("products.noStockQuery"));
    },
  }), [getMercaderia, t]);

  useEffect(() => {
    const controladorCatalogo = new AbortController();
    useStorePos.getState().limpiarResultadosMercaderias();
    if (busqueda.trim()) {
      controladorBusqueda.actualizar(
        crearFiltrosMercaderiaDesdeTexto(busqueda)
      );
    } else {
      setErrorBusqueda("");
      setBusquedaRealizada(false);
      cargarCatalogoMercaderias({ signal: controladorCatalogo.signal }).then((resultado) => {
        if (controladorCatalogo.signal.aborted) return;
        setBusquedaRealizada(true);
        if (resultado?.error) setErrorBusqueda(resultado.error);
      });
    }
    return () => {
      controladorCatalogo.abort();
      controladorBusqueda.destruir();
      useStorePos.getState().limpiarResultadosMercaderias();
    };
  }, [busqueda, controladorBusqueda, cargarCatalogoMercaderias]);

  const resumen = useMemo(() => {
    return ListaMercaderias.reduce(
      (acc, producto) => {
        const stock = obtenerStock(producto);
        acc.total += 1;
        if (stock <= 0) {
          acc.sinStock += 1;
        } else if (stock <= 5) {
          acc.bajoStock += 1;
          acc.disponibles += 1;
        } else {
          acc.disponibles += 1;
        }

        return acc;
      },
      { total: 0, disponibles: 0, bajoStock: 0, sinStock: 0 }
    );
  }, [ListaMercaderias]);

  const productosFiltrados = useMemo(() => {
    return ListaMercaderias.filter((producto) => {
      const stock = obtenerStock(producto);
      const coincideFiltro =
        filtro === "todos" ||
        (filtro === "disponibles" && stock > 0) ||
        (filtro === "bajoStock" && stock > 0 && stock <= 5) ||
        (filtro === "sinStock" && stock <= 0);
      return coincideFiltro;
    });
  }, [ListaMercaderias, filtro]);

  const textoConsulta = busqueda.trim();
  const filtrosEvaluados = evaluarFiltrosMercaderia(
    crearFiltrosMercaderiaDesdeTexto(textoConsulta)
  );
  const actualizarStock = async () => {
    useStorePos.getState().limpiarResultadosMercaderias();
    if (!textoConsulta) {
      setErrorBusqueda("");
      const resultado = await cargarCatalogoMercaderias();
      setBusquedaRealizada(true);
      if (resultado?.error) setErrorBusqueda(resultado.error);
      return;
    }
    controladorBusqueda.actualizar(
      crearFiltrosMercaderiaDesdeTexto(textoConsulta),
      { forzar: true }
    );
  };

  return (
    <section className="pos-pendientes pos-catalogo-page pos-precios-page pos-stock-page" aria-labelledby="pos-stock-titulo">
      <header className="pos-pendientes-header pos-catalogo-header">
        <div className="pos-catalogo-heading pos-section-title">
          <span className="pos-catalogo-heading-icon pos-section-icon" aria-hidden="true">
            <i className="pi pi-box" />
          </span>
          <div>
            <span className="pos-catalogo-kicker">{t("pos.title")}</span>
            <h2 id="pos-stock-titulo" className="ing-subtitulo">{t("products.availableStock")}</h2>
            <p>{t("products.stockHelp")}</p>
          </div>
        </div>
        <button
          type="button"
          className="pos-btn-secundario pos-btn-articulo-libre"
          onClick={actualizarStock}
          disabled={BuscandoMercaderia}
        >
          <i className={`pi ${BuscandoMercaderia ? "pi-spin pi-spinner" : "pi-refresh"}`} />
          {BuscandoMercaderia ? t("pos.updating") : t("common.refresh")}
        </button>
      </header>

      <div className="pos-precios-modal pos-stock-modal">
        <div className="pos-precios-topbar">
          <span className="pos-precios-note">
            <i className="pi pi-info-circle" />
            {t("products.systemReportedStock")}
          </span>
          <span className="pos-precios-count">{t("pos.productCount", { count: productosFiltrados.length })}</span>
        </div>

        <div className="pos-precios-resumen pos-stock-resumen" aria-label={t("products.stockSummary")}>
          <div>
            <span>{t("products.products")}</span>
            <strong>{resumen.total}</strong>
          </div>
          <div>
            <span>{t("products.available")}</span>
            <strong>{resumen.disponibles}</strong>
          </div>
          <div>
            <span>{t("products.lowStock")}</span>
            <strong>{resumen.bajoStock}</strong>
          </div>
          <div>
            <span>{t("pos.outOfStock")}</span>
            <strong>{resumen.sinStock}</strong>
          </div>
        </div>

        <div className="pos-precios-toolbar">
          <label className="pos-precios-search">
            <i className="pi pi-search" />
            <span className="pos-sr-only">{t("products.searchByCodeOrDescription")}</span>
            <input
              type="search"
              value={busqueda}
              onChange={(e) => setBusqueda(e.target.value)}
              placeholder={t("products.searchByCodeOrDescription")}
            />
          </label>

          <div className="pos-precios-filtros" role="group" aria-label={t("products.stockFilters")}>
            <button
              type="button"
              className={filtro === "todos" ? "activo" : ""}
              aria-pressed={filtro === "todos"}
              onClick={() => setFiltro("todos")}
            >
              {t("common.all")}
            </button>
            <button
              type="button"
              className={filtro === "disponibles" ? "activo" : ""}
              aria-pressed={filtro === "disponibles"}
              onClick={() => setFiltro("disponibles")}
            >
              {t("products.available")}
            </button>
            <button
              type="button"
              className={filtro === "bajoStock" ? "activo" : ""}
              aria-pressed={filtro === "bajoStock"}
              onClick={() => setFiltro("bajoStock")}
            >
              {t("products.lowStock")}
            </button>
            <button
              type="button"
              className={filtro === "sinStock" ? "activo" : ""}
              aria-pressed={filtro === "sinStock"}
              onClick={() => setFiltro("sinStock")}
            >
              {t("pos.outOfStock")}
            </button>
          </div>
        </div>

        <div className="pos-precios-tabla-wrap pos-stock-tabla-wrap">
          {BuscandoMercaderia && ListaMercaderias.length === 0 ? (
            <div className="pos-precios-state">
              <i className="pi pi-spin pi-spinner" />
              <strong>{t("products.loadingStock")}</strong>
            </div>
          ) : errorBusqueda ? (
            <div className="pos-precios-state"><i className="pi pi-exclamation-triangle" /><strong>{errorBusqueda}</strong></div>
          ) : !filtrosEvaluados.vacio && !filtrosEvaluados.valido ? (
            <div className="pos-precios-state"><i className="pi pi-info-circle" /><strong>{t("products.enterAtLeastOneCharacter")}</strong></div>
          ) : busquedaRealizada && ListaMercaderias.length === 0 ? (
            <div className="pos-precios-state">
              <i className="pi pi-inbox" />
              <strong>{t("common.noResults")}</strong>
            </div>
          ) : ListaMercaderias.length === 0 ? (
            <div className="pos-precios-state"><i className="pi pi-search" /><strong>{t("products.searchByCodeDescription")}</strong><span>{t("products.enterAtLeastOneCharacter")}.</span></div>
          ) : productosFiltrados.length === 0 ? (
            <div className="pos-precios-state">
              <i className="pi pi-search" />
              <strong>{t("products.noProductsToShow")}</strong>
              <span>{t("products.reviewSearchOrFilter")}</span>
            </div>
          ) : (
            <table className="pos-precios-tabla pos-stock-tabla">
              <thead>
                <tr>
                  <th>{t("pos.product")}</th>
                  <th className="pos-stock-col-stock">{t("pos.stock")}</th>
                  <th className="pos-stock-col-estado">{t("common.status")}</th>
                </tr>
              </thead>
              <tbody>
                {productosFiltrados.map((producto) => {
                  const stock = obtenerStock(producto);
                  const claseEstado = obtenerClaseEstado(stock);
                  return (
                    <tr key={producto.Id} className={claseEstado}>
                      <td data-label={t("pos.product")}>
                        <div className="pos-precios-producto-cell">
                          <strong>{producto.sDescripcion}</strong>
                          {producto.sCodigo && <span>{t("products.codeLabel", { code: producto.sCodigo })}</span>}
                        </div>
                      </td>
                      <td data-label={t("pos.stock")} className="pos-stock-col-stock">
                        <strong>{formatStock(stock, locale)}</strong>
                      </td>
                      <td data-label={t("common.status")} className="pos-stock-col-estado">
                        <span className={`pos-stock-badge ${claseEstado}`}>
                          {obtenerEstadoStock(stock, t)}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </section>
  );
};

export default PosPopupStock;
